import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import CTASection from '../components/CTASection';
import { toast } from 'sonner';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send,
  Car,
  Loader2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Phone number formatting helper
const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  if (phoneNumber.length <= 3) {
    return phoneNumber.length ? `(${phoneNumber}` : '';
  } else if (phoneNumber.length <= 6) {
    return `(${phoneNumber.slice(0, 3)})${phoneNumber.slice(3)}`;
  } else {
    return `(${phoneNumber.slice(0, 3)})${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }
};

// Full screen loader component
const FullScreenLoader = () => (
  <div className="fixed inset-0 bg-white/95 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
    <div className="relative">
      <img 
        src="/images/logo.png" 
        alt="Loading..." 
        className="w-24 h-24 object-contain animate-spin"
        style={{ animationDuration: '2s' }}
      />
    </div>
    <p className="mt-6 text-purple-600 font-semibold text-lg">Sending your message...</p>
    <p className="mt-2 text-gray-500 text-sm">Please wait while we process your request</p>
  </div>
);

const Contact = () => {
  const [formData, setFormData] = useState({
    parent_name: '',
    email: '',
    phone: '',
    child_age: '',
    message: '',
    captcha_token: 'dummy_token'
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData(prev => ({
      ...prev,
      phone: formatted
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      child_age: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setFormData({
          parent_name: '',
          email: '',
          phone: '',
          child_age: '',
          message: '',
          captcha_token: 'dummy_token'
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Full screen loader */}
      {loading && <FullScreenLoader />}
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-orange-50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="secondary" className="bg-maple-red text-white mb-6">
              🍁 Contact Us
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-navy-primary mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              We'd love to hear from you! Schedule a tour, ask questions about our program, 
              or learn more about how Maple Kids Academy can support your child's development.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-navy-primary mb-6">
                  Visit Maple Kids Academy
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  We welcome you to visit our facility and meet our team. Tours are available 
                  by appointment and give you the opportunity to see our program in action.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-6">
                <Card className="border-l-4 border-l-blue-primary">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <MapPin className="h-6 w-6 text-blue-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-navy-primary mb-2">Address</h3>
                        <p className="text-gray-600">10681 248 St</p>
                        <p className="text-gray-600">Maple Ridge, BC, Canada</p>
                        <div className="flex items-center space-x-2 mt-2">
                          <Car className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-500">Convenient parking available</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-green-50 rounded-lg">
                        <Clock className="h-6 w-6 text-green-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-navy-primary mb-2">Operating Hours</h3>
                        <p className="text-gray-600 font-medium">Monday - Friday</p>
                        <p className="text-gray-600">7:00 AM - 5:00 PM</p>
                        <p className="text-sm text-gray-500 mt-2">
                          Closed weekends and statutory holidays
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-orange-500">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-orange-50 rounded-lg">
                        <Mail className="h-6 w-6 text-orange-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-navy-primary mb-2">Email</h3>
                        <p className="text-gray-600">info@mkids.ca</p>
                        <p className="text-sm text-gray-500 mt-2">
                          We respond to all emails within 24 hours
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-purple-500">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <Phone className="h-6 w-6 text-purple-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-navy-primary mb-2">Phone</h3>
                        <p className="text-gray-600">Call for tour scheduling</p>
                        <p className="text-sm text-gray-500 mt-2">
                          Available during operating hours
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Google Maps Embed */}
              <div className="rounded-xl overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2605.8!2d-122.5194!3d49.2153!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5485d0e9a1d3c5f7%3A0x1234567890abcdef!2s10681%20248%20St%2C%20Maple%20Ridge%2C%20BC!5e0!3m2!1sen!2sca!4v1234567890"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Maple Kids Academy Location"
                ></iframe>
                <div className="bg-purple-50 p-4 text-center">
                  <p className="text-sm text-purple-700 font-medium">10681 248 St, Maple Ridge, BC</p>
                  <Button 
                    variant="link" 
                    className="text-purple-600 p-0 h-auto"
                    onClick={() => window.open('https://maps.google.com/?q=10681+248+St,+Maple+Ridge,+BC', '_blank')}
                  >
                    Get Directions →
                  </Button>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl text-navy-primary">Send us a Message</CardTitle>
                  <CardDescription>
                    We would love to hear from you. Fill out the form below or ask us any questions — we'll get back to you within 24 hours.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="parent_name">Parent/Guardian Name *</Label>
                        <Input
                          id="parent_name"
                          name="parent_name"
                          value={formData.parent_name}
                          onChange={handleInputChange}
                          required
                          placeholder="Your full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          required
                          placeholder="(000)000-0000"
                          maxLength={14}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="child_age">Child's Age *</Label>
                        <Select value={formData.child_age} onValueChange={handleSelectChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select age range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0-6 months">0-6 months</SelectItem>
                            <SelectItem value="6-12 months">6-12 months</SelectItem>
                            <SelectItem value="12-18 months">12-18 months</SelectItem>
                            <SelectItem value="18-24 months">18-24 months</SelectItem>
                            <SelectItem value="24-30 months">24-30 months</SelectItem>
                            <SelectItem value="30-36 months">30-36 months</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={5}
                        placeholder="Tell us about your child's needs, questions about our program, or preferred tour times..."
                      />
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-navy-primary mb-2">What to Expect:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Confirmation and next steps</li>
                        <li>• Personal response within 24 hours</li>
                        <li>• Discussion of your child's specific needs</li>
                        <li>• Tour scheduling at your convenience</li>
                      </ul>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-blue-primary hover:bg-blue-600" 
                      size="lg"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Tour Information */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
              Schedule Your Tour
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              We encourage all families to visit our facility before enrolling. 
              During your tour, you'll be able to:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="bg-white border-0 shadow-md">
                <CardContent className="p-6 text-center">
                  <div className="bg-blue-50 p-3 rounded-full w-fit mx-auto mb-4">
                    <MapPin className="h-6 w-6 text-blue-primary" />
                  </div>
                  <h3 className="font-semibold text-navy-primary mb-2">See Our Space</h3>
                  <p className="text-gray-600 text-sm">
                    Tour our classrooms, outdoor play area, and safety features
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white border-0 shadow-md">
                <CardContent className="p-6 text-center">
                  <div className="bg-green-50 p-3 rounded-full w-fit mx-auto mb-4">
                    <Phone className="h-6 w-6 text-green-500" />
                  </div>
                  <h3 className="font-semibold text-navy-primary mb-2">Meet Our Team</h3>
                  <p className="text-gray-600 text-sm">
                    Connect with our qualified ECE and ITE educators
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white border-0 shadow-md">
                <CardContent className="p-6 text-center">
                  <div className="bg-orange-50 p-3 rounded-full w-fit mx-auto mb-4">
                    <Clock className="h-6 w-6 text-orange-500" />
                  </div>
                  <h3 className="font-semibold text-navy-primary mb-2">Observe Activities</h3>
                  <p className="text-gray-600 text-sm">
                    See our daily routine and child-led programming in action
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white border-0 shadow-md">
                <CardContent className="p-6 text-center">
                  <div className="bg-purple-50 p-3 rounded-full w-fit mx-auto mb-4">
                    <Mail className="h-6 w-6 text-purple-500" />
                  </div>
                  <h3 className="font-semibold text-navy-primary mb-2">Ask Questions</h3>
                  <p className="text-gray-600 text-sm">
                    Discuss policies, procedures, and your child's specific needs
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-12 p-6 bg-white rounded-xl shadow-md">
              <h3 className="font-semibold text-navy-primary mb-4">Tour Guidelines</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Best Times to Visit:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Morning: 9:00 AM - 11:00 AM (active play time)</li>
                    <li>• Afternoon: 3:00 PM - 4:30 PM (outdoor activities)</li>
                    <li>• Tuesday - Thursday (less disruption to routine)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">What to Bring:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Questions about our program and policies</li>
                    <li>• Information about your child's needs</li>
                    <li>• Your child (if they're comfortable visiting)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection 
        title="Ready to Enroll?"
        subtitle="We would love to welcome your family to Maple Kids Academy. Send us a message or start your enrollment application today!"
      />
    </div>
  );
};

export default Contact;